// Утилиты для формирования payload.filters без мутаций redux state.
// Важно: API не меняем — возвращаем тот же shape фильтров, только нормализуем
// самые багогенные поля (датавременные диапазоны) и убираем дубликаты.

import { ReportFilterType } from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

const DATE_ONLY_LEN = 10;
const START_OF_DAY = " 00:00:00";
const END_OF_DAY = " 23:59:59";

// UI-специфичные поля, попадающие из дескрипторов дерева фильтров и React-компонентов.
// Бэкенд их не использует, они увеличивают payload и ломают дедупликацию.
const UI_ONLY_KEYS: readonly string[] = [
  "searchName",
  "showListPlus",
  "label",
  "description",
  "key",
  "title",
  "isLeaf",
  "disabled",
  "selectable",
  "checkable",
  "disableCheckbox",
  "children",
  "useSearch",
  "root",
  "visibleDescription",
];

/**
 * Очищает строковое значение от HTML-сущностей (&nbsp; и пр.)
 */
function cleanStringValue(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
}

function isPlainObject(value: unknown): value is AnyRecord {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === "[object Object]"
  );
}

// Стабильная сериализация для дедупликации (без сортировки исходного массива).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stableStringify(value: any): string {
  if (value === null) return "null";
  const t = typeof value;
  if (t === "string") return JSON.stringify(value);
  if (t === "number" || t === "boolean") return String(value);
  if (t === "undefined") return "undefined";
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (isPlainObject(value)) {
    const keys = Object.keys(value).sort();
    const pairs = keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`);
    return `{${pairs.join(",")}}`;
  }
  // Date / прочие объекты — как строка, чтобы не падать
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Пытается извлечь id из строки name в формате "название (id)".
 * Возвращает id как строку (для больших чисел) или null если не найден.
 */
function extractIdFromName(name: string): string | null {
  // Ищем паттерн (число) в конце строки
  const match = name.match(/\((\d+)\)\s*$/);
  if (match && match[1]) {
    return match[1];
  }
  return null;
}

/**
 * Проверяет, является ли значение валидным id (не пустым и не "[object Object]").
 */
function isValidId(id: unknown): boolean {
  if (id === undefined || id === null) return false;
  if (typeof id === 'string') {
    const trimmed = id.trim();
    // Пустая строка или результат неправильной сериализации объекта
    if (trimmed === '' || trimmed === '[object Object]') {
      return false;
    }
    return true;
  }
  if (typeof id === 'number') return !isNaN(id);
  return false;
}

/**
 * Проверяет, является ли числовая строка безопасной для преобразования в number.
 * Числа больше MAX_SAFE_INTEGER теряют точность при преобразовании.
 */
function isSafeInteger(numStr: string): boolean {
  // Проверяем, что это числовая строка
  if (!/^\d+$/.test(numStr)) return false;
  // Сравниваем как строки, чтобы избежать потери точности
  const maxSafe = '9007199254740991'; // Number.MAX_SAFE_INTEGER
  if (numStr.length < maxSafe.length) return true;
  if (numStr.length > maxSafe.length) return false;
  return numStr <= maxSafe;
}

/**
 * Нормализует элемент списка (checkeds/items), приводя id к числу если возможно.
 * Бэкенд ожидает id как число для фильтров list/lazylist.
 * Для BigInt id (которые превратились в "[object Object]") пытается извлечь id из name.
 * ВАЖНО: Большие числа (> MAX_SAFE_INTEGER) остаются строками, чтобы не потерять точность.
 * Возвращает null для невалидных элементов (пустой id).
 */
function normalizeListItem(item: unknown): { id: number | string; name: string } | null {
  // Проверяем, что item — объект
  if (!item || typeof item !== 'object') {
    return null;
  }
  
  const obj = item as { id?: unknown; name?: unknown };
  let id = obj.id;
  const name = cleanStringValue(obj.name);
  
  // Если id невалидный (например "[object Object]" от BigInt), пробуем извлечь из name
  if (!isValidId(id) && name) {
    const extractedId = extractIdFromName(name);
    if (extractedId) {
      id = extractedId;
    }
  }
  
  // Если id всё ещё невалидный — пропускаем
  if (!isValidId(id)) {
    return null;
  }
  
  // Пробуем преобразовать строку в число (только если безопасно)
  if (typeof id === 'string') {
    const trimmedId = id.trim();
    // Для очень больших чисел (BigInt) оставляем как строку — иначе потеряем точность
    if (isSafeInteger(trimmedId)) {
      const numId = parseInt(trimmedId, 10);
      return { id: numId, name };
    }
    // Большое число — оставляем как строку
    return { id: trimmedId, name };
  }
  
  // Если id уже number, проверяем не потерял ли он точность
  if (typeof id === 'number') {
    // Проверяем, не является ли это BigInt, который уже потерял точность
    // Если число больше MAX_SAFE_INTEGER, пробуем восстановить из name
    if (id > Number.MAX_SAFE_INTEGER && name) {
      const extractedId = extractIdFromName(name);
      if (extractedId) {
        return { id: extractedId, name };
      }
    }
    return { id, name };
  }
  
  // Для других типов — пропускаем
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeFilterForApi(filter: any): any {
  if (!filter || typeof filter !== "object") return filter;

  // Шаллоу-копия: не мутируем исходник из store.
  const next: AnyRecord = { ...filter };

  // Удаляем UI-специфичные поля из сохранённых фильтров (воронки, сегменты).
  for (const uiKey of UI_ONLY_KEYS) {
    delete next[uiKey];
  }

  // Удаляем поля, содержащие React-элементы или функции.
  for (const k of Object.keys(next)) {
    const val = next[k];
    if (typeof val === "function") {
      delete next[k];
      continue;
    }
    if (val && typeof val === "object" && !Array.isArray(val) && ("$$typeof" in val || "_owner" in val)) {
      delete next[k];
    }
  }

  // Очищаем &nbsp; в строковых полях верхнего уровня фильтра.
  if (typeof next.name === "string") {
    next.name = cleanStringValue(next.name);
  }
  if (typeof next.full_name === "string") {
    next.full_name = cleanStringValue(next.full_name);
  }

  // Исторический костыль: для календаря selectdates бэк ожидает datetime.
  if (
    next.type === "datetimerange" &&
    next?.calendar?.type === "selectdates" &&
    typeof next.start === "string" &&
    typeof next.end === "string" &&
    next.start.length === DATE_ONLY_LEN &&
    next.end.length === DATE_ONLY_LEN
  ) {
    next.start = next.start + START_OF_DAY;
    next.end = next.end + END_OF_DAY;
  }

  // Для list/lazylist фильтров: приводим id в checkeds к числам
  // Бэкенд ожидает id как число, а UI хранит как строку
  // Также фильтруем элементы с пустым id (они вызывают SQL ошибку)
  if ((next.type === "list" || next.type === "lazylist") && Array.isArray(next.checkeds)) {
    const originalLength = next.checkeds.length;
    next.checkeds = next.checkeds
      .map(normalizeListItem)
      .filter((item): item is { id: number | string; name: string } => item !== null);
    
  }

  // Также нормализуем items если есть
  if ((next.type === "list" || next.type === "lazylist") && Array.isArray(next.items)) {
    next.items = next.items
      .map(normalizeListItem)
      .filter((item): item is { id: number | string; name: string } => item !== null);
  }

  return next;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildApiFilters(filters: any[] | undefined | null): any[] {
  
  const input = Array.isArray(filters) ? filters : [];
  const seen = new Set<string>();
  // Важно: сохраняем порядок, потому что логика AND/OR может быть позиционной.
  const result: any[] = [];

  for (const f of input) {
    const normalized = normalizeFilterForApi(f);
    const key = stableStringify(normalized);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(normalized);
  }

  return result;
}

export function getReportTypeFromDashboardDate(date: string | undefined | null): ReportFilterType {
  switch (date) {
    case "reg":
      return "universal";
    case "ad":
      return "ad";
    case "order":
      return "order";
    case "payment":
      return "payment";
    case "web":
      return "webinar";
    case "visit":
      return "visit";
    default:
      // На дашборде могут встречаться нестандартные значения — безопасный fallback.
      return "dashboard";
  }
}

/**
 * Мержит и нормализует фильтры для API запроса.
 * 
 * Логика для дашбордов:
 * - Глобальные фильтры дашборда (globalDashboardFilters) применяются ко ВСЕМ блокам
 * - Фильтры блока (blockFilters) применяются только к конкретному блоку
 * - Результат: globalDashboardFilters AND blockFilters
 * 
 * @param params.blockFilters - Фильтры конкретного блока
 * @param params.globalDashboardFilters - Глобальные фильтры дашборда
 * @param params.isDashboardBlock - Флаг: это блок дашборда (всегда мержить фильтры)
 * @param params.commonFilters - (deprecated) Используйте globalDashboardFilters
 * @param params.includeCommon - (deprecated) Используйте isDashboardBlock
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeAndBuildApiFilters(params: {
  blockFilters?: any[] | null;
  commonFilters?: any[] | null;
  globalDashboardFilters?: any[] | null;
  includeCommon?: boolean;
  isDashboardBlock?: boolean;
  reportType?: ReportFilterType;
}): any[] {
  const { 
    blockFilters, 
    commonFilters, 
    globalDashboardFilters,
    includeCommon, 
    isDashboardBlock 
  } = params;

  // Новая логика: для блоков дашборда ВСЕГДА мержим глобальные + блочные
  if (isDashboardBlock) {
    const globalFilters = globalDashboardFilters ?? commonFilters ?? [];
    const merged = [
      ...(Array.isArray(globalFilters) ? globalFilters : []),
      ...(Array.isArray(blockFilters) ? blockFilters : []),
    ];
    return buildApiFilters(merged);
  }

  // Legacy логика для обратной совместимости
  const merged = includeCommon
    ? [
        ...(Array.isArray(blockFilters) ? blockFilters : []),
        ...(Array.isArray(commonFilters) ? commonFilters : []),
      ]
    : Array.isArray(blockFilters)
      ? blockFilters
      : [];

  return buildApiFilters(merged);
}

